/**
 * The progress ring around the platter.
 *
 * Drawn as a shader sweep rather than geometry, so it can update every frame
 * without rebuilding anything, and antialias its own leading edge.
 */

import * as THREE from 'three';
import { DIM } from '../geometry/dims.ts';
import { ACCENT } from './materials.ts';

const VERTEX = /* glsl */ `
varying vec2 vP;
void main() {
  vP = position.xy;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const FRAGMENT = /* glsl */ `
uniform float uProgress;
uniform float uOpacity;
uniform vec3 uColor;
varying vec2 vP;
void main() {
  float a = atan(vP.x, vP.y);
  if (a < 0.0) a += 6.28318530718;
  float t = a / 6.28318530718;
  float e = clamp(fwidth(t) * 1.2, 0.0006, 0.02);
  float m = 1.0 - smoothstep(uProgress - e, uProgress + e, t);
  if (m <= 0.004) discard;
  gl_FragColor = vec4(uColor, m * uOpacity);
  #include <colorspace_fragment>
}`;

export interface ProgressRing {
  fill: THREE.Mesh;
  track: THREE.Mesh;
  /** What the shader is actually drawing — the sweep, 0..1. */
  readonly progress: number;
  setProgress(p: number): void;
  setOpacity(o: number): void;
  setColor(hex: string | number): void;
  setVisible(on: boolean): void;
}

export function buildProgressRing(root: THREE.Group): ProgressRing {
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: {
      uProgress: { value: 0 },
      uOpacity: { value: 0.95 },
      uColor: { value: new THREE.Color(ACCENT) },
    },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
  });

  const fill = new THREE.Mesh(new THREE.RingGeometry(15.35, 16.15, 160, 1), material);
  fill.rotation.x = -Math.PI / 2;
  fill.position.set(DIM.platter.x, DIM.top + 0.07, DIM.platter.z);
  root.add(fill);

  const track = new THREE.Mesh(
    new THREE.RingGeometry(15.55, 15.95, 160, 1),
    new THREE.MeshBasicMaterial({
      color: 0x0b1f17,
      transparent: true,
      opacity: 0.07,
      depthWrite: false,
    }),
  );
  track.rotation.x = -Math.PI / 2;
  track.position.copy(fill.position).setY(DIM.top + 0.06);
  root.add(track);

  const uniform = (name: string): THREE.IUniform => material.uniforms[name] as THREE.IUniform;

  return {
    fill,
    track,
    get progress() {
      return uniform('uProgress').value as number;
    },
    setProgress(p) {
      uniform('uProgress').value = p;
    },
    setOpacity(o) {
      uniform('uOpacity').value = o;
    },
    setColor(hex) {
      (uniform('uColor').value as THREE.Color).set(hex);
    },
    setVisible(on) {
      fill.visible = on;
      track.visible = on;
    },
  };
}
